import "./Messages.css"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { length, map } from "@pdfking/rfunc"
import { Items, Range, Values } from "@pdfking/tfunc"
import tracking from "@pdfking/workflow/state/tracking"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import messages from "../state/messages"
import cx from "../utils/cx"

const calculate_series = (
  l: number,
  f: (v: number, i: number) => number,
  s: number,
  formatter: ((v: number) => string) | null = null
) => {
  if (l == 0) {
    return []
  }

  const result = new Array(l)
  result[l - 1] = s || 1

  for (let i = l - 2; i >= 0; i--) {
    result[i] = f(result[i + 1], i)
  }

  if (formatter) {
    return map(formatter, result)
  }

  return result
}

export const Messages = CreateRenderFunction({
  computed: {
    ...proxy(messages(), {
      messages: "messages",
      remove: "message_remove",
    } as const),
    ...proxy(tracking(), ["is_loading", "trackers"] as const),
    message_length(): number {
      return length(this.messages)
    },

    message_scale() {
      if (this.is_hover) {
        return calculate_series(this.message_length, (v: number) => v / 1.02, 1)
      }

      return calculate_series(this.message_length, (v: number) => v / 1.05, 1)
    },
    message_translate() {
      if (this.is_hover) {
        const len = this.message_length
        return map(
          (i: number) =>
            `calc((${(len - i - 1) * 100}% - ${(len - i - 1) * 8}px) * -1)`,
          Range(this.message_length)
        )
      }
      return calculate_series(
        this.message_length,
        (v: number) => v - 8,
        0,
        (v: number) => `${v}px`
      )
    },
    translations: () => make_translations("actions"),
  },
  data: {
    is_hover: false,
  },
  methods: {
    length,
    toggle_hover(hover: boolean) {
      this.is_hover = hover
    },
    Values,
  },
  render() {
    return (
      <div class="position-fixed bottom-0 w-100 pe-none" style="z-index: 3000">
        <div class="row g-0 justify-content-center">
          <div
            class="col-12 col-lg-6 position-relative"
            onFocus={() => this.toggle_hover(true)}
            onMouseLeave={() => this.toggle_hover(false)}
            onMouseOver={() => this.toggle_hover(true)}
          >
            {Values(this.messages).map((m: any, i: number) => (
              <div
                class={cx(
                  "alert clickable m-0 mb-4 shadow text-center position-absolute bottom-0 w-100 pe-auto d-flex justify-content-center align-items-center lead message",
                  `alert-${m.class || "success"}`
                )}
                key={m.id}
                // @ts-expect-error TS2349
                onClick={() => this.message_remove(m.id)}
                style={{
                  transform: `scale(${this.message_scale[i]}, ${this.message_scale[i]}) translate(0, ${this.message_translate[i]})`,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: m.content }} />
                {m.href && (
                  <a
                    class="stretched-link"
                    href={m.href}
                    target="_blank"
                    rel="noopener"
                  />
                )}
              </div>
            ))}
          </div>

          <div
            class={cx("col-12 col-lg-7 position-relative progress-tracking", {
              "progress-loading": this.is_loading,
            })}
          >
            <div class="d-flex shadow mb-0 py-2 px-1 bg-white position-absolute w-100 bottom-0 rounded-top border">
              {length(this.trackers) == 0 ? (
                <div class="progress w-100 mx-1" key="empty" />
              ) : (
                Values(this.trackers).map((tracker: any, i: number) => (
                  <div
                    class="progress text-start bg-light position-relative progress-bar-striped progress-bar-animated mx-1"
                    key={i}
                    style={{ width: `${tracker.width * 100}%` }}
                  >
                    {Items(tracker.status).map(
                      ([statusKey, status]: [string, any]) => (
                        <div
                          class={cx("progress-bar", {
                            "bg-danger": statusKey == "danger",
                            "progress-bar-striped progress-bar-animated":
                              statusKey == "loading",
                          })}
                          key={statusKey}
                          style={{ width: `${status.width * 100}%` }}
                        ></div>
                      )
                    )}
                    <div
                      class="position-absolute ms-2 text-white"
                      style="mix-blend-mode: difference"
                    >
                      {tracker.title}
                      {tracker.aggregated.done < tracker.aggregated.total
                        ? "…"
                        : null}
                      {tracker.aggregated.done >= tracker.aggregated.total && (
                        <span class="text-success">✓</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
})
